"use client";
import React from "react";

import { Chip, ChipProps } from "@clover-web-lib-sprout/chip";
import clsx from "clsx";
import { partition } from "lodash";

import "./itemMarkersChip.css"
import { useMerchant } from "@/app/providers/merchantProvider";
import { GREEN_BADGE_COLOR } from "@/constants/itemMarkers";
import type { Markers } from "@/dto/marker";
import { FEATURE_FLAG_ITEM_MARKER } from "@/utils/constants";
import { hasMerchantFeatureFlag } from "@/utils/merchantFeatureFlags";
import pom from "@/utils/pom";
import {
  getMarkerDisplayName,
  isRecommendedMarker,
} from "@/utils/recommendedItemsCategoryUtils";
import { applyDataTestId } from "@/utils/testUtils";


const getActiveMarkerIds = (markerIds: string[], markers: Markers): string[] =>
  markerIds.filter((markerId) => markers[markerId]?.isActive === true);

// Shared guard logic for both marker chip containers: feature flag check,
// empty-input checks, and active-marker filtering all live here so each
// container only has to handle its own rendering.
const useActiveMarkerIds = (
  markerIds: string[],
  markers?: Markers
): string[] => {
  const { merchant } = useMerchant();
  const isItemMarkerEnabled = hasMerchantFeatureFlag(merchant, FEATURE_FLAG_ITEM_MARKER) && !!merchant?.isRestaurant;

  if (!isItemMarkerEnabled || markerIds.length === 0 || !markers) {
    return [];
  }

  const activeMarkerIds = getActiveMarkerIds(markerIds, markers);
  return activeMarkerIds;
};

interface ItemMarkersChipContainerProps {
  readonly markerIds: string[];
  readonly markers?: Markers;
  readonly className?: string;
}
export const ItemMarkersChipContainer = ({markerIds, markers, className=""}: ItemMarkersChipContainerProps): React.ReactNode | null => {
  const activeMarkerIds = useActiveMarkerIds(markerIds, markers);
    if (activeMarkerIds.length === 0 || !markers) {
      return null;
    }

    const [recommendedMarkerIds, otherMarkerIds] = partition(
      activeMarkerIds,
      (markerId) => isRecommendedMarker(markers[markerId])
    );
    const orderedMarkerIds = [...recommendedMarkerIds, ...otherMarkerIds];

    return (
        <div
            data-testid={applyDataTestId(pom.itemModal.markers)}
            className={clsx("flex gap-2 mb-2 overflow-x-auto no-scrollbar", className)}
          >
            {orderedMarkerIds.map((markerId: string) => (
              <ItemMarkersChip 
                key={markerId}
                markerId={markerId}
                name={getMarkerDisplayName(markers[markerId])}
              />
            ))}
          </div>
    )
}

interface ItemMarkersSummaryChipContainerProps {
  readonly markerIds: string[];
  readonly markers?: Markers;
  readonly className?: string;
}
export const ItemMarkersSummaryChipContainer = ({markerIds, markers, className=""}: ItemMarkersSummaryChipContainerProps): React.ReactNode | null => {
  const activeMarkerIds = useActiveMarkerIds(markerIds, markers);
  if (activeMarkerIds.length === 0 || !markers) {
    return null;
  }

  const recommendedMarkerId = activeMarkerIds.find((markerId) =>
    isRecommendedMarker(markers[markerId])
  );
  const primaryMarkerId = recommendedMarkerId ?? activeMarkerIds[0];
  const primaryMarkerName = getMarkerDisplayName(markers[primaryMarkerId]);
  const remainingMarkerCount = activeMarkerIds.length - 1;
  const remainingMarkerId = "remaining-markers-total";

  return (
    <div
      data-testid={applyDataTestId(pom.menuItem.markers)}
      className={clsx("flex gap-2 mb-2", className)}
    >
      <ItemMarkersChip name={primaryMarkerName}  markerId={primaryMarkerId}/>
      {remainingMarkerCount > 0 && (
        <ItemMarkersChip name={`+${remainingMarkerCount}`} markerId={remainingMarkerId} />
      )}
    </div>
  );
}



export type ItemMarkerChipStatus = NonNullable<ChipProps["status"]>;
interface ItemMarkersChipProps {
  readonly markerId: string;
  readonly name: string;
  readonly status?: ItemMarkerChipStatus;
}

export const ItemMarkersChip = ({
  markerId,
  name,
  status = GREEN_BADGE_COLOR,
}: ItemMarkersChipProps): React.ReactNode | null => {
  return (
    <div className="chip-container shrink-0">
      <Chip
        status={status}
        testId={applyDataTestId(pom.menuItem.markerChip(markerId))}
      >
          {name}
      </Chip>
    </div>
  );
}